import { buildTranslate } from 'locales'
import type { PersonaId } from './simulatorTypes'

// The customer half of each guided Testing scenario: the turns the wizard sends
// into the LIVE agent on the merchant's behalf, in order.
//
// This is deliberately NOT staticTranscripts.ts. That module holds a frozen
// transcript where BOTH halves are canned and byte-pinned; here only the
// customer speaks from a script and every agent reply is real. The two datasets
// therefore have different obligations:
//
//   - staticTranscripts' copy answers only to the canonical transcript document.
//   - these turns answer to the persona's own `order_context`, because a live
//     agent reads that order record before it replies. Scenario 1's transcript
//     rows ("the burger combo", "the avocado") contradict its order (a Ribeye
//     Plate, a Caesar Salad, a Sparkling Water) — invisible while the agent's
//     side was canned too, incoherent the moment it isn't.
//
// Turn 1 is the persona's existing `opener`, the exact string master's live
// guided phase already sent, so it inherits that consistency by construction.
// Turn 2 is the follow-up detail a customer would give when the agent asks what
// else went wrong.
const t = buildTranslate('recovery_ai.sandbox')
const tPersonas = buildTranslate('recovery_ai.sandbox.personas')

// Exhaustive over PersonaId: dropping a scenario is a compile error, not a
// silently un-driven conversation at runtime.
export const CANNED_TURN_KEYS: Record<PersonaId, string[]> = {
  vip_missing_item: ['vip_missing_item.opener', 'vip_missing_item.follow_up'],
  first_time_delivery: [
    'first_time_delivery.opener',
    'first_time_delivery.follow_up',
  ],
  repeat_complainer: [
    'repeat_complainer.opener',
    'repeat_complainer.follow_up',
  ],
}

/**
 * The ordered customer turns for one persona, resolved to copy.
 *
 * Pure — no hooks, no fetch, no timers. `useCannedConversation` owns the pacing
 * and the decision of when the next turn is due.
 */
export const cannedUserMessages = (personaId: PersonaId): string[] =>
  CANNED_TURN_KEYS[personaId].map(key => tPersonas(key))

/**
 * The turn that ends the script when the agent asks something the two scripted
 * turns did not anticipate.
 *
 * Persona-independent by design: it exists precisely because the question was
 * unforeseen, so it cannot safely say anything specific about the complaint. Its
 * whole job is to signal "I have nothing more to add" and let the agent decide.
 * `useCannedConversation` caps how many times it may be sent.
 */
export const cannedClosingMessage = (): string => t('canned_closing')
